import type {
  PermisionAccessRole,
  Permision,
  PermisionScreen,
} from 'api/user/models';
import type { Token } from 'commons/modules/auth/token';

import type { ItemOption, ItemSidebarMenu } from 'context/user-role/types';

import { OTHER_KEYCLOAK_ROLES } from 'constants/globals';

import { PERMISSIONS_BY_MENU } from './constants';

export const isAllowedByOffice = (
  exclusiveByOffice: string[] | undefined,
  investmentOfficeId: string,
) => {
  if (!exclusiveByOffice || exclusiveByOffice.length === 0) return true;
  return exclusiveByOffice.includes(investmentOfficeId);
};

const getAccessRolesByAccess = (access: Record<string, string>) => {
  const accessRoles: PermisionAccessRole[] = [];
  Object.entries(access).forEach(([key, value]) => {
    accessRoles.push({ role: key, accessType: value as any });
  });
  return accessRoles;
};

const convertItemOptionByPermisionScreen = (option: ItemOption) => {
  return {
    ...option,
    accessRoles: getAccessRolesByAccess(option.accessRoles),
  };
};

const getScreensByOptions = (options: ItemOption[]) => {
  const screens: PermisionScreen[] = [];
  options.forEach((option) => {
    screens.push(convertItemOptionByPermisionScreen(option));
  });
  return screens;
};

const convertSidebarMenuToPermision = (itemMenu: ItemSidebarMenu) => {
  return {
    ...itemMenu,
    accessRoles: getAccessRolesByAccess(itemMenu.accessRoles),
    screens: getScreensByOptions(itemMenu.options),
  };
};

export const getNewPermissionsMenu = () => {
  const construirItems: Permision[] = [];
  PERMISSIONS_BY_MENU.forEach((accessByMenu) => {
    construirItems.push(convertSidebarMenuToPermision(accessByMenu));
  });
  return construirItems;
};

const validationPermisionScreens = (
  originalScreens: ItemOption[] = [],
  screens: PermisionScreen[] = [],
) => {
  const newScreens: PermisionScreen[] = [];
  let requiresUpdate = false;

  originalScreens.forEach((originalScreen) => {
    const itemScreen = screens.find(
      (screen) => screen.screenKey === originalScreen.screenKey,
    );
    if (!itemScreen) {
      newScreens.push(convertItemOptionByPermisionScreen(originalScreen));
      return;
    }
    newScreens.push(itemScreen);
  });

  if (newScreens.length !== screens.length) {
    requiresUpdate = true;
  }

  return {
    requiresUpdate: requiresUpdate,
    screens: newScreens,
  };
};

export const validationPermissionsMenu = (accessByMenu: Permision[]) => {
  const newAccessByMenu: Permision[] = [];
  let requiresUpdate = false;

  PERMISSIONS_BY_MENU.forEach((itemMenuOriginal) => {
    const itemMenu = accessByMenu.find(
      (menu) => menu.menuKey === itemMenuOriginal.menuKey,
    );
    if (!itemMenu) {
      newAccessByMenu.push(convertSidebarMenuToPermision(itemMenuOriginal));
      requiresUpdate = true;
      return;
    }

    const respPermisionScreens = validationPermisionScreens(
      itemMenuOriginal.options,
      itemMenu.screens,
    );

    if (respPermisionScreens.requiresUpdate) {
      newAccessByMenu.push({
        ...itemMenu,
        screens: respPermisionScreens.screens,
      });
      requiresUpdate = true;
      return;
    }

    newAccessByMenu.push(itemMenu);
  });

  if (newAccessByMenu.length !== accessByMenu.length) {
    requiresUpdate = true;
  }

  return {
    requiresUpdate: requiresUpdate,
    accessByMenu: newAccessByMenu,
  };
};

export const applyExclusiveByOfficeToPermissions = (
  accessByMenu: Permision[],
) => {
  return accessByMenu.map((menu) => {
    const menuFromConstants = PERMISSIONS_BY_MENU.find(
      (m) => m.menuKey === menu.menuKey,
    );

    const screens = menu.screens.map((screen) => {
      const screenFromConstants = menuFromConstants?.options.find(
        (opt) => opt.screenKey === screen.screenKey,
      );

      return {
        ...screen,
        exclusiveByOffice: screenFromConstants?.exclusiveByOffice,
      };
    });

    return {
      ...menu,
      exclusiveByOffice: menuFromConstants?.exclusiveByOffice,
      screens,
    };
  });
};

export const getSpecialOperations = (token: Token) => {
  return token?.realm_access?.roles?.includes(
    OTHER_KEYCLOAK_ROLES.specialOperations,
  );
};
